// Eager catalogue precache — pull the WHOLE catalogue down while good internet
// is available, so the app is fully usable in a genuine dead zone. A cache-first
// image strategy only caches a photo AFTER someone looks at it, which is
// worthless for a phone that starts cold with no signal (docs/plan.md, Eager
// catalogue precache).
//
// ONE PASS, NOT THREE TIERS. The corpus is ~152 products / 78 photos / ~2 MB —
// a ten-second download. So this is a single queue with a sort order (JSON +
// buyers first, then thumbnails, then covers), diffed against a dedicated
// Cache Storage bucket, fetched at concurrency 5, resumable and idempotent:
// every run re-diffs, so a sync interrupted by a dead lift simply continues
// next time. Explicitly dropped: tier state, Priority Hints, quota
// choreography — see the plan.
//
// Two consequences of the auth-gated media route land here: the precache
// fetch() is same-origin so it carries the session cookie by default, and Cache
// Storage matches on URL so the cached response is served back to a cookie-less
// request offline just fine.
import type { CatalogueManifest, Category, Finish, ManifestAsset, Material, ProductWithPhotos } from '@paradise/shared';

import { apiRequest } from '@/lib/api/http';
import { db, metaSet } from '@/lib/db';

import { refreshBuyerDirectory } from './buyer-directory';
import { useReadiness } from './readiness-store';

/**
 * The product-photo bucket. Exported because `pwa/reinstall-app.ts` must
 * PRESERVE it while wiping every other cache — see there for why. (The service
 * worker names it a third time, unavoidably: it is a separate bundle.)
 */
export const IMAGE_CACHE = 'catalogue-images-v1';
const CONCURRENCY = 5;

/**
 * Bounds on the two awaits in this file that are NOT `apiRequest` and so never
 * inherited its abort handling. Both exist for the same reason: this function
 * owns the `running` flag the readiness panel renders, so anything here that
 * can hang forever freezes that panel on "Downloading catalogue" and disables
 * the very button a user would press to retry.
 */
const PERSIST_TIMEOUT_MS = 3_000;
const ASSET_TIMEOUT_MS = 20_000;

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Floor on how often a MOUNT-triggered revalidation may re-run the pass.
 *
 * `useCatalogueProducts` / `useCategories` / `useMaterials` (lib/catalogue-data.ts)
 * each kick a pass from a mount effect, so every navigation that renders one of
 * them — opening a buyer from the board mounts two of them — used to fire the
 * whole five-request pull again the moment the previous one had finished. The
 * `running` flag below only collapses OVERLAPPING calls, never consecutive ones.
 *
 * Images cost nothing on a repeat pass (they diff against Cache Storage), but
 * the five JSON round-trips and the clear+bulkPut of four Dexie tables are real,
 * and the full-replace is what makes the live queries flicker.
 *
 * This is a floor on incidental re-pulls, NOT on the edges that genuinely mean
 * "the data may have changed" — reconnect, fresh login and the manual Download
 * button pass `force` and are never throttled (lib/offline/index.ts).
 */
const MIN_INTERVAL_MS = 60_000;

let running = false;
let lastCompletedAt: number | null = null;
let persistRequested = false;

/**
 * Ask the browser to keep our storage. Without this the cache may be evicted
 * under pressure and the phone arrives at the booth EMPTY — a silent failure
 * discovered at the worst possible moment. The one storage API that matters.
 */
async function requestPersist(): Promise<void> {
  if (persistRequested) return;
  persistRequested = true;
  if (typeof navigator !== 'undefined' && navigator.storage && typeof navigator.storage.persist === 'function') {
    try {
      // RACED, not plain-awaited. `persist()` is the FIRST await in the pass, and
      // on Android it can sit unresolved (it may consult a permission decision
      // that never arrives) — which strands `running = true` forever, freezing
      // the readiness panel on "Downloading catalogue · 0 / … photos" AND
      // disabling the "Download everything now" button that would retry it.
      // This is a nice-to-have for evictability; it must never gate the pass.
      await Promise.race([navigator.storage.persist(), sleep(PERSIST_TIMEOUT_MS)]);
    } catch {
      // Non-fatal — some browsers reject without a user gesture. The precache
      // still runs; it is just more evictable.
    }
  }
}

/** thumbs before covers; within a group, active-show products could sort first (see note). */
function assetSortKey(asset: ManifestAsset): number {
  return asset.kind === 'thumb' ? 0 : 1;
  // NOTE (later wave): docs/plan.md wants products tagged to the ACTIVE show
  // sorted to the front of each group, so this week's ~40 booth products are
  // usable first. That is one extra comparator once the tagged-itemNo set is
  // available (GET /api/events/:id/products against session.currentEventId).
  // Ordering only affects WHICH photo lands first, never completeness, so it is
  // safe to add without touching the rest of this job.
}

interface PhotoTally {
  filesExpected: number;
  filesCached: number;
  photosExpected: number;
  photosCached: number;
}

function tally(manifest: CatalogueManifest, cachedPaths: ReadonlySet<string>): PhotoTally {
  const coversExpected = new Set<number>();
  const coversCached = new Set<number>();
  let filesCached = 0;
  for (const asset of manifest.assets) {
    if (cachedPaths.has(asset.path)) filesCached += 1;
    if (asset.kind === 'cover') {
      coversExpected.add(asset.itemNo);
      if (cachedPaths.has(asset.path)) coversCached.add(asset.itemNo);
    }
  }
  return {
    filesExpected: manifest.assets.length,
    filesCached,
    photosExpected: coversExpected.size,
    photosCached: coversCached.size
  };
}

async function cachedPathSet(cache: Cache): Promise<Set<string>> {
  const keys = await cache.keys();
  return new Set(keys.map(req => new URL(req.url).pathname));
}

/**
 * Fetch one asset and store it. Same-origin → cookie rides along for the
 * auth-gated media route.
 *
 * The timeout is load-bearing. This is a raw `fetch`, not `apiRequest`, so it
 * did not inherit that module's abort handling — and a hall wifi that accepts
 * the connection then delivers nothing leaves it pending indefinitely. Because
 * `drainPool` awaits every worker, one such asset hangs the entire pass, and
 * `running` never clears. A skipped photo costs nothing: every run re-diffs
 * against Cache Storage, so the next pass simply picks it up again.
 */
async function fetchAndCache(cache: Cache, asset: ManifestAsset): Promise<boolean> {
  try {
    const response = await fetch(asset.path, {
      credentials: 'include',
      signal: AbortSignal.timeout(ASSET_TIMEOUT_MS)
    });
    if (!response.ok) return false;
    await cache.put(asset.path, response.clone());
    return true;
  } catch {
    // Offline, aborted, or a transient failure — the next run re-diffs and retries.
    return false;
  }
}

/** Simple bounded worker pool over the missing assets. */
async function drainPool(cache: Cache, missing: ManifestAsset[], onProgress: () => Promise<unknown>): Promise<void> {
  let cursor = 0;
  let sinceProgress = 0;
  async function worker(): Promise<void> {
    for (;;) {
      const index = cursor;
      cursor += 1;
      const asset = missing[index];
      if (!asset) return;
      await fetchAndCache(cache, asset);
      sinceProgress += 1;
      // Update the readiness counter every few files so the panel ticks up
      // during a long download without a re-diff per file.
      if (sinceProgress >= CONCURRENCY) {
        sinceProgress = 0;
        await onProgress();
      }
    }
  }
  await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
}

/**
 * Run one precache pass. Idempotent and resumable — safe to call on load, after
 * login, on reconnect, and from the manual "Download everything now" button.
 * Concurrent calls collapse into the one already running.
 *
 * `force` bypasses the MIN_INTERVAL_MS floor. Pass it from the connectivity,
 * login and manual-refresh edges; leave it off for the mount-effect
 * revalidations, which are speculative by nature.
 */
export async function syncCatalogue(options: { force?: boolean } = {}): Promise<void> {
  if (running) return;
  if (!options.force) {
    if (lastCompletedAt !== null && Date.now() - lastCompletedAt < MIN_INTERVAL_MS) return;
    // HYDRATE-ONLY. An unforced call is a mount effect firing speculatively; it
    // now runs only when this device has NO catalogue at all. Once there is
    // one, refreshing it is an explicit act — app start, reconnect, a fresh
    // login, or the Download button, all of which pass `force`. Without this,
    // opening a buyer (which mounts two catalogue hooks) re-pulled ~63 KB of
    // JSON and clear+bulkPut'd four Dexie tables, and that full-replace is what
    // makes every live query on the screen flicker.
    //
    // The forced edges are load-bearing, not decoration: the image precache is
    // resumable only because something re-runs the diff (see startOffline).
    if ((await db.catalogue.count()) > 0) return;
    // The count above is an await, so another caller may have started in the
    // meantime — re-check rather than running the whole pass twice.
    if (running) return;
  }
  running = true;
  const readiness = useReadiness.getState();
  readiness.patch({ running: true, lastError: null });

  try {
    await requestPersist();

    // ── Group 1: text first — catalogue products + buyer directory ──────────
    const productsResult = await apiRequest<ProductWithPhotos[]>('/api/catalogue/products');
    if (productsResult.kind === 'ok') {
      await db.transaction('rw', db.catalogue, async () => {
        await db.catalogue.clear();
        await db.catalogue.bulkPut(productsResult.data);
      });
      readiness.patch({ productsCached: productsResult.data.length, productsExpected: productsResult.data.length });
    } else if (productsResult.kind === 'offline') {
      readiness.patch({ productsCached: await db.catalogue.count() });
    } else {
      // `unauthenticated` or `error` — the categories/materials/manifest pulls
      // below would fail identically, so stop here rather than pressing on.
      // Crucially, SURFACE it: without this branch a not-yet-signed-in session
      // (or a 401) left the whole mirror empty with no message at all — the
      // "nothing in IndexedDB, no explanation" symptom. Set lastError so the
      // Readiness panel says why instead of just "Not fully cached".
      readiness.patch({
        lastError:
          productsResult.kind === 'unauthenticated'
            ? 'Sign in while you have signal to download the catalogue for offline use.'
            : productsResult.message
      });
      return;
    }

    // Categories, materials and finishes — a few rows of text, negligible cost,
    // but without them a device with no signal renders blank category chips and
    // blank material names on every product (docs/plan.md; the taxonomy is
    // mirrored full-replace, same as `catalogue` above).
    const categoriesResult = await apiRequest<Category[]>('/api/categories');
    if (categoriesResult.kind === 'ok') {
      await db.transaction('rw', db.categories, async () => {
        await db.categories.clear();
        await db.categories.bulkPut(categoriesResult.data);
      });
    }

    const materialsResult = await apiRequest<Material[]>('/api/materials');
    if (materialsResult.kind === 'ok') {
      await db.transaction('rw', db.materials, async () => {
        await db.materials.clear();
        await db.materials.bulkPut(materialsResult.data);
      });
    }

    const finishesResult = await apiRequest<Finish[]>('/api/finishes');
    if (finishesResult.kind === 'ok') {
      await db.transaction('rw', db.finishes, async () => {
        await db.finishes.clear();
        await db.finishes.bulkPut(finishesResult.data);
      });
    }

    // Buyer directory downloads first alongside the JSON (a few hundred rows).
    await refreshBuyerDirectory();

    // ── Manifest + image diff ───────────────────────────────────────────────
    const manifestResult = await apiRequest<CatalogueManifest>('/api/catalogue/manifest');
    if (manifestResult.kind !== 'ok') {
      // No manifest → can't precache images this pass. Reflect whatever is
      // already cached so the panel isn't blank, then bail (next run retries).
      if (manifestResult.kind === 'offline') {
        readiness.patch({ productsCached: await db.catalogue.count() });
      } else if (manifestResult.kind === 'error') {
        readiness.patch({ lastError: manifestResult.message });
      }
      return;
    }
    const manifest = manifestResult.data;
    await metaSet('catalogueVersion', manifest.version);

    // Cache Storage exists only in a secure context. Over plain HTTP `caches`
    // is not even declared, so this surfaced on the readiness panel as
    // "caches is not defined" — accurate, and useless to the person reading it
    // before a show. Dexie has no such restriction, which is why the panel can
    // report every product cached and not one photo.
    if (!window.isSecureContext || typeof caches === 'undefined') {
      readiness.patch({
        lastError: 'Photos need the https:// address — over plain http the browser gives the app no offline storage.'
      });
      return;
    }

    const cache = await caches.open(IMAGE_CACHE);

    // Prune anything no longer in the manifest so `cachedPaths.size ===
    // assets.length` stays an exact completeness assertion. Filenames are
    // content-hashed, so a changed photo is a new path and its predecessor
    // falls out here.
    const wantedPaths = new Set(manifest.assets.map(a => a.path));
    for (const req of await cache.keys()) {
      if (!wantedPaths.has(new URL(req.url).pathname)) await cache.delete(req);
    }

    const applyTally = async (): Promise<Set<string>> => {
      const paths = await cachedPathSet(cache);
      const t = tally(manifest, paths);
      readiness.patch({
        filesExpected: t.filesExpected,
        filesCached: t.filesCached,
        photosExpected: t.photosExpected,
        photosCached: t.photosCached
      });
      return paths;
    };

    // Reuse the set applyTally just enumerated rather than reading cache.keys() twice.
    const cachedPaths = await applyTally();
    const missing = manifest.assets
      .filter(a => !cachedPaths.has(a.path))
      .sort((x, y) => assetSortKey(x) - assetSortKey(y));

    await drainPool(cache, missing, applyTally);
    await applyTally();
  } catch (cause) {
    useReadiness.getState().patch({ lastError: cause instanceof Error ? cause.message : 'catalogue sync failed' });
  } finally {
    running = false;
    lastCompletedAt = Date.now();
    useReadiness.getState().patch({ running: false, lastRunAt: lastCompletedAt });
  }
}
